/**
 * @module Toast
 *
 * Toast notification system built on Sonner. Provides success, error, info, and warning variants.
 *
 * @see {@link https://ui.shadcn.com/docs/components/sonner Shadcn Sonner}
 * @see {@link https://sonner.emilkowal.ski/ Sonner Documentation}
 *
 * @example
 * // Setup: Add Toaster to your app root
 * <Toaster />
 *
 * @example
 * // Show toast notifications
 * toast("Default message");
 * toast.success("Operation completed");
 * toast.error("Something went wrong");
 * toast.info("Information");
 * toast.warning("Warning message");
 *
 * @example
 * // With action button
 * toast({
 *   title: "Item deleted",
 *   action: {
 *     label: "Undo",
 *     onClick: () => undoDelete(),
 *   },
 * });
 *
 * @example
 * // Promise toast
 * const id = toast.promise(promise, {
        loading: { title: "Saving document..." },
        success: (data) => ({
          title: `${data.name} saved successfully!`,
          duration: 10_000,
        }),

        error: { title: "Failed to save document", duration: 10_000 },
      });
 */
import { cva } from "class-variance-authority";
import type React from "react";
import type { ReactNode } from "react";
import { type ExternalToast, Toaster as SonnerToaster, toast as sonnerToast } from "sonner";
import { Button, type ButtonProps, cn } from "..";
import { Check, CircleX, InfoIcon, Loader, ThumbsDown, ThumbsUp, TriangleAlert, X } from "../icons";
type ToasterProps = React.ComponentProps<typeof SonnerToaster>;
type ToastVariant = "error" | "info" | "loading" | "success" | "warning" | "feedback";
const GLOBAL_TOASTER_ID = "global";
interface GlobalToastAction {
  label: string;
  onClick: () => void;
}
interface GlobalToastProps {
  action?: GlobalToastAction;
  description?: string;
  id: string | number;
  onNegative?: () => void;
  onPositive?: () => void;
  title?: string;
  variant: ToastVariant;
}
export interface GlobalToastOptions extends ExternalToast {
  action?: GlobalToastAction;
  description?: string;
  onNegative?: () => void;
  onPositive?: () => void;
  title?: string;
}
const GlobalToastTitle = ({
  className,
  children
}: {
  className?: string;
  children: ReactNode;
}) => {
  if (!children) {
    return;
  }
  return <div className={cn("font-semibold text-black text-sm dark:text-white w-[90%] text-balance", className)} data-sentry-component="GlobalToastTitle" data-sentry-source-file="toast.tsx">
      {children}
    </div>;
};
const GlobalToastDescription = ({
  children
}: {
  children: ReactNode;
}) => {
  if (!children) {
    return;
  }
  return <div className="font-normal text-gray-700 text-sm dark:text-gray-200 " data-sentry-component="GlobalToastDescription" data-sentry-source-file="toast.tsx">
      {children}
    </div>;
};
const globalToastIconVariants = cva("size-7 shrink-0 rounded-full flex items-center justify-center [&_svg]:size-4", {
  variants: {
    variant: {
      error: "bg-red-50 dark:bg-red-950 [&_svg]:text-red-500 [&_svg]:dark:text-red-400",
      info: "bg-gray-100 dark:bg-gray-800 [&_svg]:text-gray-700 [&_svg]:dark:text-gray-200",
      loading: "bg-gray-100 dark:bg-gray-800 [&_svg]:text-gray-700 [&_svg]:dark:text-gray-200",
      success: "bg-green-50 dark:bg-green-950 [&_svg]:text-green-500 [&_svg]:dark:text-green-400",
      warning: "bg-yellow-50 dark:bg-yellow-950 [&_svg]:text-yellow-500 [&_svg]:dark:text-yellow-400"
    }
  },
  defaultVariants: {
    variant: "info"
  }
});
const GlobalToastIcon = ({
  variant
}: {
  variant: ToastVariant;
}) => {
  if (variant === "error") {
    return <span className={cn(globalToastIconVariants({
      variant
    }))}>
        <CircleX />
      </span>;
  }
  if (variant === "info") {
    return <span className={cn(globalToastIconVariants({
      variant
    }))}>
        <InfoIcon />
      </span>;
  }
  if (variant === "loading") {
    return <span className={cn(globalToastIconVariants({
      variant
    }))}>
        <Loader className="animate-spin" style={{
        animationDuration: "1.5s"
      }} />
      </span>;
  }
  if (variant === "success") {
    return <span className={cn(globalToastIconVariants({
      variant
    }))}>
        <Check />
      </span>;
  }
  if (variant === "warning") {
    return <span className={cn(globalToastIconVariants({
      variant
    }))}>
        <TriangleAlert className="pb-px" />
      </span>;
  }
};
const globalToastLeftBorderVariants = cva("absolute top-0 left-0 h-full w-1", {
  variants: {
    variant: {
      error: "bg-red-500 dark:bg-red-400",
      info: "bg-gray-700 dark:bg-gray-200",
      loading: "bg-gray-700 dark:bg-gray-200",
      success: "bg-green-500 dark:bg-green-400",
      warning: "bg-yellow-500 dark:bg-yellow-400",
      feedback: "bg-transparent"
    }
  },
  defaultVariants: {
    variant: "info"
  }
});
const GlobalToastLeftBorder = ({
  variant
}: {
  variant: ToastVariant;
}) => <div className={cn(globalToastLeftBorderVariants({
  variant
}))} data-sentry-component="GlobalToastLeftBorder" data-sentry-source-file="toast.tsx" />;
const GlobalToastDismissButton = ({
  toastId
}: {
  toastId: number | string;
}) => <Button className="absolute top-1 right-1" onClick={() => sonnerToast.dismiss(toastId)} size="icon" variant="subtle" data-sentry-element="Button" data-sentry-component="GlobalToastDismissButton" data-sentry-source-file="toast.tsx">
    <X className="size-5" data-sentry-element="X" data-sentry-source-file="toast.tsx" />
  </Button>;
const GlobalToastActionButton = ({
  onClick,
  ...props
}: ButtonProps) => <Button onClick={onClick} size="sm" variant="light" {...props} data-sentry-element="Button" data-sentry-component="GlobalToastActionButton" data-sentry-source-file="toast.tsx" />;
const globalToastClassnames = {
  base: "flex min-h-16 w-full xs:w-80 md:w-90 items-center justify-between rounded-lg border-1 border-gray-150 border-solid bg-white px-4 py-3 text-black dark:border-gray-800 dark:text-white gap-2 shadow-md dark:bg-black in-[.footer-player-active]:-translate-y-16 relative overflow-hidden",
  // Feedback prompts sit flatter and roomier than alerts — no icon, two inline buttons
  feedback: "gap-4 shadow-75 dark:bg-clay-900"
};
export const GlobalToast = (props: GlobalToastProps) => {
  const {
    id,
    title,
    description,
    variant,
    action,
    onPositive,
    onNegative
  } = props;
  if (variant === "feedback") {
    const handleAnswer = (callback?: () => void) => {
      callback?.();
      sonnerToast.dismiss(id);
    };
    return <div className={cn(globalToastClassnames.base, globalToastClassnames.feedback)} key={id}>
        <GlobalToastLeftBorder variant={variant} />
        <p className="font-semibold text-black text-sm dark:text-white">
          {title ?? "Did the results match your search?"}
        </p>

        <div className="flex shrink-0 items-center gap-4">
          <Button aria-label="Yes, the results matched" icon onClick={() => handleAnswer(onPositive)} size="sm" variant="transparent">
            <ThumbsUp className="size-5" />
          </Button>
          <Button aria-label="No, the results did not match" icon onClick={() => handleAnswer(onNegative)} size="sm" variant="transparent">
            <ThumbsDown className="size-5" />
          </Button>
        </div>
      </div>;
  }
  return <div className={globalToastClassnames.base} key={id} data-sentry-component="GlobalToast" data-sentry-source-file="toast.tsx">
      <GlobalToastLeftBorder variant={variant} data-sentry-element="GlobalToastLeftBorder" data-sentry-source-file="toast.tsx" />
      <GlobalToastIcon variant={variant} data-sentry-element="GlobalToastIcon" data-sentry-source-file="toast.tsx" />
      <div className="flex w-full flex-col gap-0.5">
        <GlobalToastTitle className={action ? "w-full" : "w-[90%]"} data-sentry-element="GlobalToastTitle" data-sentry-source-file="toast.tsx">
          {title}
        </GlobalToastTitle>
        <GlobalToastDescription data-sentry-element="GlobalToastDescription" data-sentry-source-file="toast.tsx">{description}</GlobalToastDescription>
      </div>

      {action ? <GlobalToastActionButton onClick={action.onClick}>
          {action.label}
        </GlobalToastActionButton> : <GlobalToastDismissButton toastId={id} />}
    </div>;
};

/**
 * Toast container component. Place once at app root.
 * Positioned bottom-right by default, shows up to 4 toasts.
 */
const Toaster: React.FC<ToasterProps> = ({
  toastOptions,
  ...props
}) => <SonnerToaster id={GLOBAL_TOASTER_ID} position="bottom-right" visibleToasts={4} {...props} data-sentry-element="SonnerToaster" data-sentry-component="Toaster" data-sentry-source-file="toast.tsx" />;
const createGlobalToast = (variant: ToastVariant, options?: GlobalToastOptions) => {
  const {
    action,
    description,
    title,
    onPositive,
    onNegative,
    id: customId,
    ...rest
  } = options || {};
  const toastId = sonnerToast.custom(id => <GlobalToast action={action} description={description} id={customId || id} onNegative={onNegative} onPositive={onPositive} title={title} variant={variant} />, {
    ...(customId !== undefined && {
      id: customId
    }),
    toasterId: GLOBAL_TOASTER_ID,
    ...rest
  });

  // Return the custom ID if provided, otherwise return Sonner's generated ID
  return customId === undefined ? toastId : customId;
};

// Helper method to normalize toast arguments
const normalizeToastArgs = (messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions): GlobalToastOptions | undefined => {
  if (typeof messageOrOptions === "string") {
    return {
      ...options,
      title: messageOrOptions
    };
  }
  return messageOrOptions;
};
interface PromiseToastOptions<T> {
  error: GlobalToastOptions | ((error: unknown) => GlobalToastOptions);
  loading: GlobalToastOptions;
  success: GlobalToastOptions | ((data: T) => GlobalToastOptions);
}
interface ToastFunction {
  custom: typeof sonnerToast.custom;
  dismiss: typeof sonnerToast.dismiss;
  error: {
    (message: string, options?: GlobalToastOptions): string | number;
    (options: GlobalToastOptions): string | number;
  };
  feedback: {
    (message: string, options?: GlobalToastOptions): string | number;
    (options: GlobalToastOptions): string | number;
  };
  getHistory: typeof sonnerToast.getHistory;
  getToasts: typeof sonnerToast.getToasts;
  info: {
    (message: string, options?: GlobalToastOptions): string | number;
    (options: GlobalToastOptions): string | number;
  };
  loading: {
    (message: string, options?: GlobalToastOptions): string | number;
    (options: GlobalToastOptions): string | number;
  };
  message: {
    (message: string, options?: GlobalToastOptions): string | number;
    (options: GlobalToastOptions): string | number;
  };
  promise: <T>(promise: Promise<T> | (() => Promise<T>), options: PromiseToastOptions<T>) => Promise<T>;
  success: {
    (message: string, options?: GlobalToastOptions): string | number;
    (options: GlobalToastOptions): string | number;
  };
  warning: {
    (message: string, options?: GlobalToastOptions): string | number;
    (options: GlobalToastOptions): string | number;
  };
  (message: string, options?: GlobalToastOptions): string | number;
  (options: GlobalToastOptions): string | number;
}
const toast: ToastFunction = ((messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions) => createGlobalToast("info", normalizeToastArgs(messageOrOptions, options))) as ToastFunction;
toast.success = ((messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions) => createGlobalToast("success", normalizeToastArgs(messageOrOptions, options))) as ToastFunction["success"];
toast.error = ((messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions) => createGlobalToast("error", normalizeToastArgs(messageOrOptions, options))) as ToastFunction["error"];
toast.info = ((messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions) => createGlobalToast("info", normalizeToastArgs(messageOrOptions, options))) as ToastFunction["info"];
toast.warning = ((messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions) => createGlobalToast("warning", normalizeToastArgs(messageOrOptions, options))) as ToastFunction["warning"];
toast.loading = ((messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions) => createGlobalToast("loading", normalizeToastArgs(messageOrOptions, options))) as ToastFunction["loading"];
toast.message = ((messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions) => createGlobalToast("info", normalizeToastArgs(messageOrOptions, options))) as ToastFunction["message"];
toast.feedback = ((messageOrOptions?: GlobalToastOptions | string, options?: GlobalToastOptions) => createGlobalToast("feedback", {
  // Feedback requires an answer: stay open until a thumb is clicked.
  duration: Number.POSITIVE_INFINITY,
  dismissible: false,
  ...normalizeToastArgs(messageOrOptions, options)
})) as ToastFunction["feedback"];
toast.promise = <T,>(promiseOrFn: Promise<T> | (() => Promise<T>), states: PromiseToastOptions<T>): Promise<T> => {
  const toastId = createGlobalToast("loading", states.loading);
  const promise = typeof promiseOrFn === "function" ? promiseOrFn() : promiseOrFn;
  promise.then(data => {
    const successToastOptions = typeof states.success === "function" ? states.success(data) : states.success;
    const {
      title,
      description,
      action,
      ...rest
    } = successToastOptions || {};
    sonnerToast.custom(id => <GlobalToast action={action} description={description} id={id} title={title} variant="success" />, {
      id: toastId,
      toasterId: GLOBAL_TOASTER_ID,
      ...rest
    });
  }).catch(error => {
    const errorToastOptions = typeof states.error === "function" ? states.error(error) : states.error;
    const {
      title,
      description,
      action,
      ...rest
    } = errorToastOptions || {};
    sonnerToast.custom(id => <GlobalToast action={action} description={description} id={id} title={title} variant="error" />, {
      id: toastId,
      toasterId: GLOBAL_TOASTER_ID,
      ...rest
    });
  });
  return promise;
};
toast.custom = sonnerToast.custom;
toast.dismiss = sonnerToast.dismiss;
toast.getHistory = sonnerToast.getHistory;
toast.getToasts = sonnerToast.getToasts;
export { Toaster, type ToasterProps, toast };